import { useRef, useState, useEffect } from "react";
import {
  motion,
  animate,
  useInView,
  useMotionValue,
  useSpring,
} from "framer-motion";

export const CountUp = ({ value, className }) => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-40px" });
  const match = String(value).match(/^([\d.]+)(.*)$/);
  const target = parseFloat(match?.[1] || "0");
  const suffix = match?.[2] || "";
  const decimals = (match?.[1]?.split(".")[1] || "").length;
  const [display, setDisplay] = useState(`0${".".padEnd(decimals > 0 ? decimals + 1 : 0, "0")}`);

  useEffect(() => {
    if (!inView) return;
    const controls = animate(0, target, {
      duration: 1.8,
      ease: [0.22, 1, 0.36, 1],
      onUpdate: (v) => setDisplay(v.toFixed(decimals)),
    });
    return () => controls.stop();
  }, [inView, target, decimals]);

  return (
    <span ref={ref} className={className}>
      {display}
      {suffix}
    </span>
  );
};

export const Magnetic = ({ children, strength = 0.3, className = "" }) => {
  const ref = useRef(null);
  const x = useSpring(useMotionValue(0), { stiffness: 160, damping: 14 });
  const y = useSpring(useMotionValue(0), { stiffness: 160, damping: 14 });

  const onMove = (e) => {
    const r = ref.current?.getBoundingClientRect();
    if (!r) return;
    x.set((e.clientX - (r.left + r.width / 2)) * strength);
    y.set((e.clientY - (r.top + r.height / 2)) * strength);
  };
  const reset = () => {
    x.set(0);
    y.set(0);
  };

  return (
    <motion.div
      ref={ref}
      onMouseMove={onMove}
      onMouseLeave={reset}
      style={{ x, y }}
      className={`inline-block ${className}`}
    >
      {children}
    </motion.div>
  );
};

export const TiltCard = ({ children, className = "", max = 7 }) => {
  const ref = useRef(null);
  const rx = useSpring(useMotionValue(0), { stiffness: 220, damping: 20 });
  const ry = useSpring(useMotionValue(0), { stiffness: 220, damping: 20 });

  const onMove = (e) => {
    const r = ref.current?.getBoundingClientRect();
    if (!r) return;
    const px = (e.clientX - r.left) / r.width - 0.5;
    const py = (e.clientY - r.top) / r.height - 0.5;
    ry.set(px * max);
    rx.set(-py * max);
  };
  const reset = () => {
    rx.set(0);
    ry.set(0);
  };

  return (
    <motion.div
      ref={ref}
      onMouseMove={onMove}
      onMouseLeave={reset}
      style={{ rotateX: rx, rotateY: ry, transformPerspective: 900 }}
      className={className}
    >
      {children}
    </motion.div>
  );
};

export const Kicker = ({ children, className = "" }) => (
  <span
    className={`mb-3 inline-flex items-center gap-2.5 font-mono text-xs uppercase tracking-[0.25em] text-ember ${className}`}
  >
    <svg
      aria-hidden
      className="h-3.5 w-3.5 animate-spin-slow text-toothcyan"
      viewBox="0 0 24 24"
      fill="none"
    >
      <circle
        cx="12"
        cy="12"
        r="10"
        stroke="currentColor"
        strokeWidth="1.6"
        strokeDasharray="3 4"
      />
      <circle cx="12" cy="12" r="3.5" fill="currentColor" opacity="0.5" />
    </svg>
    {children}
  </span>
);
